import {PoolDisplayGroup} from '../../models/pool-display-group';
import {PoolFilters} from '../../models/pool-filters';
import {PoolList} from '../../models/pool-list';
import * as _ from 'lodash-es';
import {ConfigurationScope} from '../configuration/configuration.models';
import {PoolCategories, PoolsMenuScope} from './pool.models';

// ---------------------------------------------------------------------------
// Public interface for the single-pass result
// ---------------------------------------------------------------------------

export interface ParsedPoolsComplete {
  menu: PoolsMenuScope.ParsedPoolsMenu;
  filters: PoolsMenuScope.PoolFiltersByCategory;
  extra: PoolsMenuScope.PoolsExtraModel;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------
const FANTASY_CATEGORY_CODE = 'FANTASY';

const getGroupKeys = (
  pool: PoolsMenuScope.Pool,
  categoryCode: string,
  allowNewGroupCategoryCreation: boolean
): string[] => {
  if (categoryCode === FANTASY_CATEGORY_CODE) {
    return [pool.sport_code];
  }
  if (allowNewGroupCategoryCreation) {
    return [...new Set([pool.sport_code, ...(pool.sport_codes || [])])];
  }
  return [pool.sport_code];
};

const buildPoolList = (
  categoryPools: PoolsMenuScope.Pool[],
  categoryCode: string,
  localSettings: ConfigurationScope.localSettings
): PoolList => {
  const result = new PoolList();
  const typeOrder: Record<string, number> = {
    ...(localSettings.poolTypesOrder || {}),
    ...(localSettings.overridesPoolTypesOrder || {})
  };
  const allowNew = !!localSettings.allowNewGroupCategoryCreation;

  // O(1) group lookup — built during the first pass, reused in subsequent passes
  const groupMap = new Map<string, PoolDisplayGroup>();

  // --- Pass 1: IN_PLAY ---
  const runningPools = categoryPools.filter(p => p.status === 'IN_PLAY' && !p.free_play_only);
  const runningByKey = new Map<string, PoolsMenuScope.Pool[]>();
  for (const pool of runningPools) {
    for (const sportCode of getGroupKeys(pool, categoryCode, allowNew)) {
      const key = `${sportCode}.${pool.type_code}`;
      if (!runningByKey.has(key)) runningByKey.set(key, []);
      runningByKey.get(key)!.push(pool);
    }
  }
  for (const [key, items] of runningByKey) {
    const [s0, s1, s2] = key.split('.');
    const ids = _.map(
      _.orderBy(items, ['sched_start', (p: PoolsMenuScope.Pool) => Number(p.headline_prize)], ['desc', 'desc']),
      'id'
    );
    groupMap.set(key, new PoolDisplayGroup(key, s0 || null, s1 || null, s2 || null, [], ids));
  }

  // --- Pass 2: OFFICIAL ---
  const officialPools = categoryPools.filter(p => p.status === 'OFFICIAL' && !p.free_play_only);
  const officialByKey = new Map<string, PoolsMenuScope.Pool[]>();
  for (const pool of officialPools) {
    for (const sportCode of getGroupKeys(pool, categoryCode, allowNew)) {
      const key = `${sportCode}.${pool.type_code}`;
      if (!officialByKey.has(key)) officialByKey.set(key, []);
      officialByKey.get(key)!.push(pool);
    }
  }
  for (const [key, items] of officialByKey) {
    const [s0, s1, s2] = key.split('.');
    const ids = _.map(
      _.orderBy(items, ['sched_start', (p: PoolsMenuScope.Pool) => Number(p.headline_prize)], ['desc', 'desc']),
      'id'
    );
    const existing = groupMap.get(key);
    if (existing) {
      existing.official_pools = ids;
    } else {
      groupMap.set(key, new PoolDisplayGroup(key, s0 || null, s1 || null, s2 || null, [], [], ids));
    }
  }

  // --- Pass 3: OPEN ---
  const openPools = categoryPools.filter(p => p.status === 'OPEN' && !p.free_play_only);
  const openByKey = new Map<string, PoolsMenuScope.Pool[]>();
  for (const pool of openPools) {
    for (const sportCode of getGroupKeys(pool, categoryCode, allowNew)) {
      const key = `${sportCode}.${pool.type_code}`;
      if (!openByKey.has(key)) openByKey.set(key, []);
      openByKey.get(key)!.push(pool);
    }
  }
  for (const [key, items] of openByKey) {
    const [s0, s1, s2] = key.split('.');
    const ids = _.map(
      _.orderBy(items, ['sched_start', (p: PoolsMenuScope.Pool) => Number(p.headline_prize)], ['asc', 'desc']),
      'id'
    );
    const existing = groupMap.get(key);
    if (existing) {
      existing.pools = ids;
    } else {
      groupMap.set(key, new PoolDisplayGroup(key, s0 || null, s1 || null, s2 || null, ids));
    }
  }

  result.free_play_only = _.map(
    _.orderBy(
      categoryPools.filter(p => p.status === 'OPEN' && p.free_play_only),
      (p: PoolsMenuScope.Pool) => Number(p.headline_prize),
      'desc'
    ),
    'id'
  );
  result.open_pools = openPools.map(p => p.id);
  result.running_pools = runningPools.map(p => p.id);
  result.official_pools = officialPools.map(p => p.id);
  result.up_next = _.map(_.take(_.orderBy(openPools, 'sched_start'), 4), 'id');
  result.big_jackpots = _.map(
    _.take(_.orderBy(openPools, (p: PoolsMenuScope.Pool) => Number(p.headline_prize), 'desc'), 4),
    'id'
  );
  result.display_groups = _.orderBy(
    Array.from(groupMap.values()),
    (g: PoolDisplayGroup) => typeOrder[String(g.code)] ?? Number.MAX_SAFE_INTEGER,
    'asc'
  );

  return result;
};

const buildFilters = (
  poolsInScope: PoolsMenuScope.Pool[],
  category: ConfigurationScope.SportCategory,
  localSettings: ConfigurationScope.localSettings
): PoolFilters => {
  const poolTypesOrder: Record<string, number> = {
    ...(localSettings.poolTypesOrder || {}),
    ...(localSettings.overridesPoolTypesOrder || {})
  };

  const filters = new PoolFilters();
  const sportCodes: string[] = category.description?.sport_codes || [];

  filters.all = sportCodes.join(',') || undefined;
  filters.view = sportCodes;
  filters.sport = [...new Set(poolsInScope.map(p => p.sport_code))];

  const poolTypes = [...new Set(poolsInScope.map(p => `${p.sport_code}.${p.type_code}`))];
  filters.pool_type = poolTypes.sort((a, b) => {
    const oA = poolTypesOrder[a] ?? Number.MAX_SAFE_INTEGER;
    const oB = poolTypesOrder[b] ?? Number.MAX_SAFE_INTEGER;
    return oA - oB;
  });

  filters.pool = [...new Set(poolsInScope.map(p => `${p.sport_code}.${p.type_code}.${p.leg_num}`))];

  const subCodes = [...new Set(poolsInScope.map(p => p.sport_sub_code).filter(Boolean))];
  filters.sport_sub_code = subCodes.sort((a, b) => {
    const [, subA = ' '] = (a || '').split('_');
    const [, subB = ' '] = (b || '').split('_');
    return (subA + a).localeCompare(subB + b);
  });

  return filters;
};

// ---------------------------------------------------------------------------
// Main export: single-pass parsing of all enabled categories
// ---------------------------------------------------------------------------

export const parsePoolsComplete = (
  pools: PoolsMenuScope.Pool[],
  coreSettings: ConfigurationScope.coreSettings,
  localSettings: ConfigurationScope.localSettings,
  currentExtra: PoolsMenuScope.PoolsExtraModel
): ParsedPoolsComplete => {
  // O(n) pool dict build
  const poolsById: PoolsMenuScope.PoolsDict = {};
  for (const pool of pools) {
    poolsById[String(pool.id)] = pool;
  }

  // micrositeType whitelist: when set, only process listed categories plus ALL.
  // When not set, process everything from coreSettings.
  const micrositeType = localSettings.micrositeType || [];
  const enabledCodes: Set<string> | null = micrositeType.length > 0
    ? new Set<string>([PoolCategories.ALL, ...micrositeType])
    : null;

  const allCategories = coreSettings.sport_categories || [];
  const enabledCategories = enabledCodes
    ? allCategories.filter(cat => enabledCodes.has(cat.code))
    : allCategories;

  const categories: Record<string, PoolList> = {};
  const filters: PoolsMenuScope.PoolFiltersByCategory = {};
  const poolCounts: Record<string, number> = {};
  const bdg: PoolsMenuScope.Pool[] = [];

  for (const category of enabledCategories) {
    const sportCodes = new Set<string>(category.description?.sport_codes || []);

    // One filter pass per category (not three)
    const categoryPools = pools.filter(p =>
      sportCodes.has(p.sport_code) ||
      (p.sport_codes?.some(c => sportCodes.has(c)) ?? false)
    );

    categories[category.code] = buildPoolList(categoryPools, category.code, localSettings);

    const openFilter = categoryPools.filter(p => p.status === 'OPEN');
    const liveFilter = categoryPools.filter(p => p.status === 'OPEN' || p.status === 'IN_PLAY');
    filters[category.code] = {
      OPEN: buildFilters(openFilter, category, localSettings),
      LIVE: buildFilters(liveFilter, category, localSettings),
      ALL: buildFilters(liveFilter, category, localSettings),
    };

    poolCounts[category.code] = category.pools_count;

    // BDG is RACING-specific; driven by category code, not a hardcoded string
    if (category.code === PoolCategories.RACING) {
      for (const pool of categoryPools) {
        if (pool.status === 'OPEN' && pool.best_dividend_guarantee) {
          bdg.push(pool);
        }
      }
    }
  }

  return {
    menu: { pools: poolsById, categories },
    filters,
    extra: { ...currentExtra, poolCounts, BDG: bdg }
  };
};

// ---------------------------------------------------------------------------
// parseMenu — fully data-driven, iterates whatever categories exist
// ---------------------------------------------------------------------------

export const parseMenu = (parsedMenu: PoolsMenuScope.ParsedPoolsMenu): Record<string, string[]> => {
  try {
    const result: Record<string, string[]> = {};
    for (const [code, poolList] of Object.entries(parsedMenu.categories || {})) {
      const sportCodes = new Set<string>();
      for (const group of poolList?.display_groups || []) {
        if (group.pools?.length > 0 && group.sport_code) {
          sportCodes.add(group.sport_code);
        }
      }
      result[code] = Array.from(sportCodes);
    }
    return result;
  } catch (error) {
    return {};
  }
};

